import { Box, css, ListItem, styled } from "@mui/material";
import {
    ForwardedRef,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { FixedSizeList, ListOnScrollProps } from "react-window";

import { Breadcrumbs } from "./breadcrumbs";
import { BreadcrumbNodeSpec, ListNode, Node, ObjectTreeNodeProps, Tree } from "./types";
import { defaultBreadcrumbNodeLabel, getParentPaths } from "./utils";

type ObjectTreeProps<T> = {
    width: number;
    height: number;
    itemSize: number;
    tree: Tree<T>;
    list: ListNode<T>[];
    /**
     * ID of the item to scroll to. Scroll is executed only once `scrollTo` changes.
     */
    scrollTo?: string;
    /**
     * Bumped by the parent on every new selection so the scroll re-fires even
     * when `scrollTo` resolves to the same path as the previous selection.
     */
    scrollToVersion?: number;
    /**
     * Called with the currently visible row range whenever it changes. Lets the
     * parent lazily load more of a folder's children as the user scrolls toward
     * the end of what's loaded, instead of eagerly draining everything.
     */
    onVisibleRangeChange?: (visibleStartIndex: number, visibleStopIndex: number) => void;
    ListItem: (props: ObjectTreeNodeProps<T>) => JSX.Element;
    buildBreadcrumbs?: (nodes: Node<T>[]) => BreadcrumbNodeSpec<T>[];
    /** Disables horizontal overflow — items fill the viewport width and names truncate with ellipsis. */
    noHorizontalScroll?: boolean;
    /**
     * Controls the contextual header pinned above the list:
     * - `"breadcrumbs"` (default) — shows the V1 breadcrumb trail as the user scrolls deep into the tree.
     * - `"sticky"` — pins the depth-0 group header row as the user scrolls into its children (V2).
     * - `"none"` — no header.
     */
    headerMode?: "breadcrumbs" | "sticky" | "none";
    /** Controls scroll position when scrolling to a selected item. "top" (default) = V1 behavior; "center" = always center, no already-visible guard. */
    scrollAlignment?: "top" | "center";
};

export type ObjectTreeRef = {
    rescroll: () => void;
};

export const ObjectTree = forwardRef(function ObjectTree<T>(
    {
        width,
        height,
        itemSize,
        tree,
        list,
        scrollTo,
        scrollToVersion,
        onVisibleRangeChange,
        ListItem: ListItemFn,
        buildBreadcrumbs = defaultBreadcrumbBuilder,
        noHorizontalScroll = false,
        headerMode = "breadcrumbs",
        scrollAlignment = "top",
    }: ObjectTreeProps<T>,
    ref: ForwardedRef<ObjectTreeRef>,
) {
    const containerRef = useRef<FixedSizeList | null>(null);
    const listOuterRef = useRef<HTMLDivElement | null>(null);
    const [listClientWidth, setListClientWidth] = useState<number | null>(null);
    const [topVisibleItemIndex, setTopVisibleItemIndex] = useState(-1);
    const scrollFullfilledRef = useRef(false);
    const [scrollGeneration, setScrollGeneration] = useState(0);
    // Range of rows react-window currently has on screen. Tracked so the
    // scroll-to effect can leave an already-visible row in place instead of
    // yanking it to the top on every selection. react-window owns this state,
    // so (unlike a manually tracked scroll offset) it stays accurate across
    // list rebuilds and clamped scroll positions.
    const visibleRangeRef = useRef<{ start: number; stop: number }>({ start: -1, stop: -1 });

    useEffect(() => {
        scrollFullfilledRef.current = scrollTo ? false : true;
    }, [scrollTo, scrollToVersion]);

    // Track the list's content width (clientWidth excludes the scrollbar track) so the
    // sticky header doesn't overlap the scrollbar. Reading clientWidth during render is
    // stale when the window is undocked/resized without a prop change, so we use a
    // ResizeObserver to stay in sync with actual DOM layout.
    useEffect(() => {
        const el = listOuterRef.current;
        if (!el) {
            return;
        }
        setListClientWidth(el.clientWidth);
        const ro = new ResizeObserver(() => setListClientWidth(el.clientWidth));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            rescroll: () => {
                scrollFullfilledRef.current = false;
                setScrollGeneration((s) => s + 1);
            },
        }),
        [],
    );

    // Scroll to scrollTo document
    useEffect(() => {
        if (!scrollTo || scrollFullfilledRef.current) {
            return;
        }
        let index = list.findIndex(
            (item) =>
                item.node.path === scrollTo ||
                item.nodes?.some((node) => node.path === scrollTo) ||
                item.nodeGroup?.some((node) => node.path === scrollTo),
        );
        if (index !== -1) {
            if (scrollAlignment === "center") {
                // V2: center the row using an explicit scroll offset rather than
                // scrollToItem so that react-window's "already visible" check
                // never silently skips the scroll.
                const desiredOffset = index * itemSize - Math.round((height - itemSize) / 2);
                const maxOffset = Math.max(0, list.length * itemSize - height);
                const clampedOffset = Math.max(0, Math.min(desiredOffset, maxOffset));
                containerRef.current?.scrollTo(clampedOffset);
                // If the target is near the end of the list the desired offset is
                // clamped and the row lands near the bottom instead of the center.
                // Leave fulfilled=false so the effect re-fires when background
                // loaders extend the list and centering is no longer clamped.
                if (desiredOffset <= maxOffset) {
                    scrollFullfilledRef.current = true;
                }
            } else {
                // Only scroll when the target row is not already *fully* on screen.
                // react-window reports the first/last row as visible even when they
                // are only partially in view, and the breadcrumb bar overlays the
                // topmost visible row once the list is scrolled. Both edges are
                // therefore unreliable: require a one-row margin at the top (covered
                // by the breadcrumb or sticky header) and bottom (clipped) so an
                // edge-aligned target still scrolls into clear view instead of being
                // left half-hidden.
                const hasHeaderOverlay = headerMode !== "none";
                const { start, stop } = visibleRangeRef.current;
                if (start >= 0 && stop > start) {
                    const coveredByHeader = hasHeaderOverlay && start > 0 ? 1 : 0;
                    const firstFullyVisible = start + coveredByHeader;
                    const lastFullyVisible = stop - 1;
                    if (index >= firstFullyVisible && index <= lastFullyVisible) {
                        scrollFullfilledRef.current = true;
                        return;
                    }
                }

                // Scroll so the selected item is second in the list to adjust for
                // the header overlay (breadcrumb or sticky header) covering the top row.
                // Exception for the last item, because it's not visible with this adjustment.
                const headerAdjustment = hasHeaderOverlay && index !== list.length - 1 ? -1 : 0;
                const itemsFitIntoView = Math.round(height / itemSize);
                const maxIndex = Math.max(0, list.length - itemsFitIntoView);
                index = Math.min(maxIndex, Math.max(0, index + headerAdjustment));
                containerRef.current?.scrollTo(index * itemSize);
                scrollFullfilledRef.current = true;
            }
        }
    }, [scrollTo, scrollToVersion, scrollGeneration, list, height, itemSize, scrollAlignment, headerMode]);

    const breadcrumbs = useMemo(() => {
        if (headerMode !== "breadcrumbs" || topVisibleItemIndex <= 1 || topVisibleItemIndex >= list.length) {
            return null;
        }

        const item = list[topVisibleItemIndex];
        const result: Node<T>[] = [];
        for (const path of getParentPaths(item.node.path)) {
            const node = tree[path];
            if (!node) {
                return null;
            }
            result.push(node);
        }

        return buildBreadcrumbs(result);
    }, [list, topVisibleItemIndex, tree, headerMode, buildBreadcrumbs]);

    const stickyContentItem = useMemo((): ListNode<T> | null => {
        if (headerMode !== "sticky") {
            return null;
        }
        const topIndex = Math.max(0, topVisibleItemIndex - 1);
        for (let i = topIndex; i >= 0; i--) {
            if (list[i]?.level === 0) {
                return i < topIndex ? list[i] : null;
            }
        }
        return null;
    }, [headerMode, topVisibleItemIndex, list]);

    const handleBreadcrumbClick = useCallback(
        (node: Node<T>) => {
            if (node.path === "") {
                containerRef.current?.scrollToItem(0, "start");
            } else {
                const index = list.findIndex(
                    (e) => e.node.path === node.path || e.nodes?.some((n) => n.path === node.path),
                );
                if (index !== -1) {
                    const breadcrumbAdjustment = -1;
                    containerRef.current?.scrollToItem(index + breadcrumbAdjustment, "start");
                }
            }
        },
        [list],
    );

    const handleScroll = useCallback(
        (e: ListOnScrollProps) => {
            const breadcrumbAdjustment = 1;
            const topIndex = Math.max(0, Math.min(list.length - 1, Math.floor(e.scrollOffset / itemSize)));
            const lastIndex = Math.max(0, Math.min(list.length - 1, Math.ceil((e.scrollOffset + height) / itemSize)));
            if (list.length === 0 || lastIndex === 0) {
                setTopVisibleItemIndex(0);
                return;
            }

            setTopVisibleItemIndex(Math.min(list.length - 1, topIndex + breadcrumbAdjustment));
        },
        [height, list.length, itemSize],
    );

    return (
        <>
            <FixedSizeList
                height={height}
                width={width}
                itemSize={itemSize}
                itemCount={list.length}
                overscanCount={5}
                ref={containerRef}
                outerRef={listOuterRef}
                onScroll={handleScroll}
                onItemsRendered={({ visibleStartIndex, visibleStopIndex }) => {
                    visibleRangeRef.current = { start: visibleStartIndex, stop: visibleStopIndex };
                    onVisibleRangeChange?.(visibleStartIndex, visibleStopIndex);
                }}
                className="object-tree-scroll-container"
                style={noHorizontalScroll ? { overflowX: "hidden" } : undefined}
                // ListOuter removes the focus outline (blue ring) that react-window applies to its
                // outer scroll container by default. It also sets tabIndex={-1} so the container
                // is not reachable via keyboard tab navigation.
                outerElementType={ListOuter}
                innerElementType={noHorizontalScroll ? undefined : HorizontalScrollInner}
            >
                {({ index, style }) => {
                    const item = list[index];

                    return (
                        <ListItem
                            style={
                                noHorizontalScroll
                                    ? { ...style, overflow: "hidden" }
                                    : { ...style, width: "max-content", minWidth: "100%" }
                            }
                            key={item.node.path}
                            component="div"
                            disablePadding
                        >
                            {noHorizontalScroll ? (
                                <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}>
                                    <ListItemFn item={item} />
                                </div>
                            ) : (
                                <ListItemWrapper>
                                    <ListItemFn item={item} />
                                </ListItemWrapper>
                            )}
                        </ListItem>
                    );
                }}
            </FixedSizeList>

            <Breadcrumbs breadcrumbs={breadcrumbs} onClick={handleBreadcrumbClick} />

            {stickyContentItem && (
                <StickyGroupHeader width={listClientWidth ?? width} itemSize={itemSize}>
                    <ListItemFn item={stickyContentItem} isSticky />
                </StickyGroupHeader>
            )}
        </>
    );
}) as <T>(props: ObjectTreeProps<T> & { ref?: ForwardedRef<ObjectTreeRef> }) => JSX.Element;

const ListItemWrapper = styled(Box)(
    ({ theme }) => css`
        flex: 1 0 auto;
        display: flex;
        align-items: center;
        gap: ${theme.spacing(0.5)};
        margin-top: ${theme.spacing(0.5)};
        margin-bottom: ${theme.spacing(0.5)};
        min-width: 100%;
    `,
);

const HorizontalScrollInner = forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & { style?: React.CSSProperties }
>(function HorizontalScrollInner({ style, ...rest }, ref) {
    return <div ref={ref} {...rest} style={{ ...style, width: "max-content", minWidth: "100%" }} />;
});

const ListOuter = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & { style?: React.CSSProperties }>(
    function ListOuter({ style, ...rest }, ref) {
        return <div ref={ref} {...rest} tabIndex={-1} style={{ ...style, outline: "none" }} />;
    },
);

function StickyGroupHeader({
    width,
    itemSize,
    children,
}: {
    width: number;
    itemSize: number;
    children: React.ReactNode;
}) {
    return (
        <div
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                width,
                height: itemSize,
                zIndex: 1,
                background: "white",
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}>{children}</div>
        </div>
    );
}

function defaultBreadcrumbBuilder<T>(nodes: Node<T>[]) {
    return nodes.map((node) => {
        return { node, label: defaultBreadcrumbNodeLabel(node) };
    });
}
